import log from "loglevel";
import ProjectWorld, { ProjectEventType, type AccessEntry, type ProjectUser, ShareLink, createServerEvent } from "../ProjectWorld";
import type { BaseEvent, BaseProjectionResult, BaseEventInput, EventProcessor } from "@utilitydelta/clientside-event-sourcing";
import { AccessLevel, ServerEvent } from "@utilitydelta/eventplanedb-client";

export const MINOR_VERSION: number = 1;

export interface ProvideAccess_LocalEvent extends BaseEvent<ProjectEventType> {
    tp: typeof ProjectEventType.ProvideAccess;
    userId?: string; // ID of the user receiving access
    clientId: string; // ID of the client receiving access
    shareLinkId?: string; // ID of the share link used
    accessLevel: AccessLevel;
}

export interface ProvideAccess_ProjectionResult extends BaseProjectionResult {
    kind: 'ProvideAccess_ProjectionResult';
    shareLink?: ShareLink; // The share link used, if any
    accessEntry?: AccessEntry; // The created access entry
    projectUser: ProjectUser; // User record with updated access
}

export interface ProvideAccess_EventInputs extends BaseEventInput {
    kind: 'ProvideAccess_EventInputs';
    userId?: string; // ID of the user receiving access
    clientId: string; // ID of the client receiving access
    shareLinkId?: string; // ID of the share link used
    accessLevel: AccessLevel;
}

export type ProvideAccess_EventProcessorType = EventProcessor<ProjectEventType, ProvideAccess_EventInputs, ProvideAccess_LocalEvent, ProvideAccess_ProjectionResult, ProvideAccess_LocalEvent, ServerEvent<ProjectEventType>>;

export class ProvideAccess_EventProcessor implements ProvideAccess_EventProcessorType {

    private projectWorld: ProjectWorld;
    constructor(pw: ProjectWorld) { this.projectWorld = pw; }

    public get shouldEncrypt(): boolean { return true; }
    public get eventType(): ProjectEventType { return ProjectEventType.ProvideAccess; }
    public get eventTypeMinorVersion(): number { return MINOR_VERSION; }
    public get undoEventType(): ProjectEventType { return ProjectEventType.ProvideAccess; }
    public get undoEventTypeMinorVersion(): number { return MINOR_VERSION; }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public createUndoEvent = (_undoEvent: ProvideAccess_LocalEvent, _originalEventProjectionResult: ProvideAccess_ProjectionResult): boolean => {
        return false;
    }

    public projectOntoWorld = (event: ProvideAccess_LocalEvent, addUpdatedEntitiesToDirtySet: boolean, returnProjectionResults: boolean): ProvideAccess_ProjectionResult | undefined => {
        // Check for invalid event
        const id = event.userId ?? event.clientId;

        if (!id) {
            log.warn("Invalid ProvideAccess event: missing required fields (userId or clientId)", event);
            return undefined;
        }

        // Check if share link exists
        const shareLink = event.shareLinkId ? this.projectWorld.shareLinks.maybeGet(event.shareLinkId) : undefined;
        const accessLevel = shareLink === undefined ? event.accessLevel : shareLink.accessLevel;

        let projectUser = this.projectWorld.projectUsers.maybeGet(id);
        if (projectUser === undefined) {
            projectUser = {
                id,
                isOAuthUser: event.userId !== undefined,
                isDeleted: false,
                pi: this.projectWorld.projectMetadata.id,
                lastEventTime: { [event.tp]: event.et } as Record<ProjectEventType, number>,
                createdByClientId: event.ci,
                createdByUserId: event.ui,
                createdOn: event.et,
                modifiedByClientId: event.ci,
                modifiedByUserId: event.ui,
                modifiedOn: event.et,
                accessLevel: accessLevel, // Set initial access level from share link
                lastSeenOn: event.et,
                accessUpdatedOn: event.et,
                color: 0,
                randomUsername: undefined,
            }
            this.projectWorld.projectUsers.upsert(projectUser);
        } else {
            // Verify this event is newer than the last processed event of this type
            if (projectUser.lastEventTime[event.tp] !== undefined && projectUser.lastEventTime[event.tp] > event.et) {
                return undefined;
            }

            if (projectUser.accessLevel === accessLevel) {
                log.warn("ProvideAccess event ignored: user already has same access level", event);
                return undefined; // No change in access level
            }

            projectUser.accessLevel = accessLevel; // Update user's access level based on share link
            projectUser.accessUpdatedOn = event.et; // Update access updated timestamp

            projectUser.lastEventTime[event.tp] = event.et;
            projectUser.modifiedByClientId = event.ci;
            projectUser.modifiedByUserId = event.ui;
            projectUser.modifiedOn = event.et;
        }

        if (addUpdatedEntitiesToDirtySet) {
            this.projectWorld.addEntityToSave('projectUsers', projectUser);
        }

        // Create new access entry
        let accessEntry: AccessEntry | undefined = undefined;
        if (event.shareLinkId !== undefined) {
            accessEntry = {
                id: `${id}_${event.shareLinkId}`,
                isDeleted: false,
                pi: this.projectWorld.projectMetadata.id,
                lastEventTime: { [event.tp]: event.et } as Record<ProjectEventType, number>,
                createdByClientId: event.ci,
                createdByUserId: event.ui,
                createdOn: event.et,
                modifiedByClientId: event.ci,
                modifiedByUserId: event.ui,
                modifiedOn: event.et,
                clientId: event.clientId,
                userId: event.userId,
                shareLinkId: event.shareLinkId,
                accessTime: event.et,
                accessLevel: accessLevel
            };
            this.projectWorld.accessEntries.upsert(accessEntry);

            if (addUpdatedEntitiesToDirtySet) {
                this.projectWorld.addEntityToSave('accessEntries', accessEntry);
            }
        }

        if (shareLink !== undefined) {
            shareLink.usageCount = (shareLink.usageCount ?? 0) + 1; // Increment usage count of the share link
            if (addUpdatedEntitiesToDirtySet) {
                this.projectWorld.addEntityToSave('shareLinks', shareLink);
            }
        }

        return !returnProjectionResults ? undefined : {
            kind: 'ProvideAccess_ProjectionResult',
            accessEntry,
            projectUser,
            shareLink
        };
    }

    public createLocalEvent = (eventToPopulate: ProvideAccess_LocalEvent, userInputs: ProvideAccess_EventInputs): boolean => {

        const id = userInputs.userId ?? userInputs.clientId;
        if (id === undefined) return false;

        if (userInputs.shareLinkId !== undefined) {
            const accessEntryId = `${id}_${userInputs.shareLinkId}`;
            const accessEntry = this.projectWorld.accessEntries.maybeGet(accessEntryId);
            if (accessEntry !== undefined && accessEntry.accessLevel === userInputs.accessLevel) return false;
        }

        eventToPopulate.userId = userInputs.userId;
        eventToPopulate.clientId = userInputs.clientId;
        eventToPopulate.accessLevel = userInputs.accessLevel;
        eventToPopulate.shareLinkId = userInputs.shareLinkId;

        return true;
    }

    public toServerEvent = async (localEvent: ProvideAccess_LocalEvent): Promise<ServerEvent<ProjectEventType>> => {
        const eventData = {
            clientId: localEvent.clientId,
            userId: localEvent.userId,
            accessLevel: localEvent.accessLevel,
            shareLinkId: localEvent.shareLinkId
        };
        const eventDataString = JSON.stringify(eventData);
        return createServerEvent(localEvent, eventDataString);
    }

    public fromServerEvent = async (localEventToPopulate: ProvideAccess_LocalEvent, serverEvent: ServerEvent<ProjectEventType>): Promise<void> => {
        const eventData = JSON.parse(atob(serverEvent.ev))
        Object.assign(localEventToPopulate, eventData);
    }
}